// The allow-list `custom-html` and every text element's markup are re-serialised against.
//
// The settings grammar already refuses `<script`, `javascript:`, `data:text/html` and inline `on*=`
// at save time, but it never sees `<iframe srcdoc>`, `<object>`, `<embed>` or `<svg onbegin>` — all
// four are ordinary strings to a blacklist. This file is the other end: nothing reaches the DOM
// unless it is named here, so a vector nobody thought of is dropped by default rather than kept.

/** Tags kept as themselves. Flow content only: nothing that loads, embeds, scripts or submits. */
export const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'p', 'br', 'hr', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'small', 'sub', 'sup', 'mark',
  'code', 'pre', 'kbd', 'samp', 'var', 'blockquote', 'q', 'cite', 'abbr', 'time', 'address', 'bdi', 'bdo', 'wbr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'a', 'img', 'figure', 'figcaption', 'picture',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
  'section', 'article', 'aside', 'header', 'footer', 'nav', 'main', 'details', 'summary'
]);

/**
 * Tags dropped with everything inside them. A tag outside this list and outside `ALLOWED_TAGS` is
 * unwrapped — its text survives, the tag does not — because a merchant's `<my-widget>` around a
 * paragraph should not swallow the paragraph. These families must swallow theirs: their contents are
 * markup for another parser (`srcdoc`, a plugin, an SVG document, a raw-text island), so unwrapping
 * them would hand that parser exactly what it asked for.
 */
export const DROPPED_SUBTREES: ReadonlySet<string> = new Set([
  'script', 'style', 'noscript', 'template', 'xmp', 'plaintext', 'listing',
  'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'portal', 'canvas',
  'svg', 'math',
  'form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'fieldset', 'legend', 'output',
  'head', 'base', 'link', 'meta', 'title', 'html', 'body',
  'audio', 'video', 'source', 'track', 'map', 'area'
]);

/**
 * Tags that close themselves, so the stack never waits for an end tag that is not coming. The dropped
 * ones are here too: without them `<embed src=…>` would look like an element with a subtree, and the
 * skip that follows a dropped tag would swallow the rest of the page looking for an `</embed>`.
 */
export const VOID_TAGS: ReadonlySet<string> = new Set([
  'br', 'hr', 'img', 'wbr', 'col',
  'embed', 'source', 'track', 'input', 'link', 'meta', 'base', 'area', 'param', 'frame'
]);

/**
 * The tags a start tag implies the end of. A browser closes an open `<p>` when the next `<p>` opens;
 * so must this, or a merchant who never closed one gets their whole page nested inside it.
 */
export const IMPLIED_END: Readonly<Record<string, readonly string[]>> = {
  p: ['p'],
  li: ['li', 'p'],
  dt: ['dt', 'dd', 'p'],
  dd: ['dt', 'dd', 'p'],
  tr: ['tr', 'td', 'th', 'p'],
  td: ['td', 'th', 'p'],
  th: ['td', 'th', 'p'],
  summary: ['p']
};

/** Attributes any allowed tag may carry. `id` and `name` are absent on purpose: both clobber `document`. */
const GLOBAL_ATTRIBUTES: ReadonlySet<string> = new Set(['class', 'dir', 'lang', 'title', 'role']);

/** Attributes only the tag that owns them may carry, so an `href` cannot appear on a `<td>`. */
const TAG_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height', 'loading', 'decoding'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
  col: ['span'],
  colgroup: ['span'],
  ol: ['start', 'reversed'],
  li: ['value'],
  time: ['datetime'],
  blockquote: ['cite'],
  q: ['cite'],
  details: ['open']
};

/** The attributes read as a URL, which are the only ones a scheme check has to run on. */
const URL_ATTRIBUTES: ReadonlySet<string> = new Set(['href', 'src', 'cite']);

/** The schemes a URL may name. Everything else — `javascript:`, `vbscript:`, `file:` — is dropped. */
const SAFE_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** The one `data:` payload an image may carry; `data:text/html` and `data:image/svg+xml` both script. */
const DATA_IMAGE_RE = /^data:image\/(?:png|jpeg|jpg|gif|webp|avif);base64,[a-zA-Z0-9+/=]+$/;

// Strips the whitespace and control characters a scheme can hide behind: `java&#9;script:`.
const stripBlanks = (value: string): string =>
  Array.from(value).filter((ch) => {
    const code = ch.codePointAt(0) ?? 0;
    return code > 0x20 && code !== 0x7f && code !== 0xa0 && code !== 0xfeff && !(code >= 0x200b && code <= 0x200f);
  }).join('');

/** The named entities worth decoding before a scheme is read; the rest cannot forge one. */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", colon: ':', tab: '\t', newline: '\n', sol: '/', nbsp: ' '
};

// Decodes the entities a browser decodes inside an attribute, so `&#106;avascript:` is read as what it is.
export function decodeEntities(raw: string): string {
  return raw.replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z]+);?/g, (whole, body: string) => {
    if (body[0] !== '#') return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
    const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
  });
}

// Reports whether a URL may stay, after the entities and the control characters a scheme can hide behind.
export function isSafeUrl(raw: string, allowDataImage: boolean): boolean {
  const value = stripBlanks(decodeEntities(raw));
  if (value === '') return false;
  if (DATA_IMAGE_RE.test(value)) return allowDataImage;
  const colon = value.indexOf(':');
  const path = value.search(/[/?#]/);
  if (colon === -1 || (path !== -1 && path < colon)) return true;
  return SAFE_SCHEMES.has(value.slice(0, colon + 1).toLowerCase());
}

// Reports whether one attribute may stay on one tag, before its value is looked at.
function isAllowedAttribute(tag: string, name: string): boolean {
  if (name.startsWith('on')) return false;
  if (name.startsWith('aria-')) return /^aria-[a-z]+$/.test(name);
  return GLOBAL_ATTRIBUTES.has(name) || (TAG_ATTRIBUTES[tag] ?? []).includes(name);
}

/** One attribute that survived the allow-list, ready to be written back out. */
export type SafeAttribute = { name: string; value: string };

// Keeps the attributes one tag may carry, dropping any whose URL names a scheme that can script.
export function cleanAttributes(tag: string, attrs: readonly SafeAttribute[]): SafeAttribute[] {
  const kept = attrs.filter(({ name, value }) =>
    isAllowedAttribute(tag, name) && (!URL_ATTRIBUTES.has(name) || isSafeUrl(value, tag === 'img')));
  return tag === 'a' ? withLinkRel(kept) : kept;
}

// Forces a new-tab link to drop its opener, which is the one thing an allowed `target` can still leak.
function withLinkRel(attrs: readonly SafeAttribute[]): SafeAttribute[] {
  const blank = attrs.some(({ name, value }) => name === 'target' && value.toLowerCase() === '_blank');
  if (!blank) return [...attrs];
  return [...attrs.filter(({ name }) => name !== 'rel'), { name: 'rel', value: 'noopener noreferrer' }];
}

// Escapes text so a character a merchant typed can never re-open the parser this file just closed.
export const escapeText = (raw: string): string =>
  raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** An ampersand that does not already begin a character reference such as `&amp;`, `&#39;` or `&nbsp;`. */
const BARE_AMPERSAND_RE = /&(?!(?:#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});)/g;

// Escapes text read out of markup, keeping the references it already holds: a text node decodes them only to characters.
export const escapeMarkupText = (raw: string): string =>
  raw.replace(BARE_AMPERSAND_RE, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Escapes an attribute value, quotes included, so no value can end its own quoting and start an attribute.
export const escapeAttribute = (raw: string): string => escapeText(raw).replace(/"/g, '&quot;');
